import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import BarCharts from '../Charts/BarCharts/BarCharts';
import PieCharts from '../Charts/PieCharts/PieCharts';
import AuthService from '../../Services/AuthService';
import { useNavigate } from 'react-router-dom';
import { BarChart3, PieChart as PieChartIcon, ClipboardCheck } from 'lucide-react';
import logo from "../../Assets/Images/Navbar/LoaderLogo.png";
import {
    barOptions,
    barDataset,
    doughnutOptions,
    capSeries,
    formatCount,
} from '../../helpers/chartTheme';
const Dashboard = () => {
    const navigate = useNavigate();

    const [DashboardData, setDashboardData] = useState([])
    const [Loader, setLoader] = useState(false)
    const [chartLoader, setChartLoader] = useState(false)
    const [DashboardTabChartData, setDashboardTabChartData] = useState([])
    const [BarChatName, setBarChatName] = useState('');
    const [activeCardLabel, setActiveCardLabel] = useState('Resolved Property Queries');
    useEffect(() => {
        const userDetailsFromStorage = JSON.parse(localStorage.getItem('userDetails'));
        getDashboardChatData(userDetailsFromStorage, 'Resolved Property Queries')
        setBarChatName('Resolved Property Queries')
        setActiveCardLabel('Resolved Property Queries')
        getDashboardData(userDetailsFromStorage)
    }, [])
    useEffect(() => {
        const handlePopState = (e) => {
            e.preventDefault();
            navigate(1);
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);
    const getDashboardChatData = async (x, y) => {
        setChartLoader(true)
        let obj = {
            name: y,
            year: new Date().getFullYear(),
            engineerId: x.civilEng_id
        }
        const response = await AuthService.getTabDataForCivilEngineer(obj)
        if (response.message === 'Success') {
            setDashboardTabChartData(response.data)
        }
        setChartLoader(false)
    }
    const getDashboardData = async (x) => {
        setLoader(true)
        let obj = {
            engineerId: x.civilEng_id
        }
        const response = await AuthService.TotalCountForCivilEngineer(obj)
        if (response?.message === 'Success') {
            setDashboardData(response?.data)
            setLoader(false)
        } else {
            setLoader(false)
        }
    }
    const labels = DashboardTabChartData.labels;

    /* Single series, so no legend box — the card title names it. Slot 0 is the
       brand teal, which is what this chart already used. */
    const chartData = {
        labels: labels,
        datasets: [barDataset(BarChatName, DashboardTabChartData.data, 0)],
    };

    const chartOptions = barOptions({ seriesCount: 1, valueFormatter: formatCount });

    /* Share of the year's total.
       -------------------------------------------------------------------------
       This card previously rendered a 12-slice pie — one wedge per month — in a
       hand-built teal ramp where January and June shared a colour and December
       repeated June's. Three problems: 12 slices in an all-pairs form are not
       separable at any palette; months are ordered data, so a categorical
       ramp threw the order away; and the bar chart beside it already shows the
       same numbers by month, better.

       It now answers a question the bar chart does not: how concentrated is the
       year? Top three months plus a folded "Other", which respects the 4-slice
       all-pairs cap, and the legend carries the values. */
    const monthEntries = (labels || []).map((label, index) => ({
        label,
        value: Number(DashboardTabChartData.data?.[index]) || 0,
    })).filter((entry) => entry.value > 0);

    const capped = capSeries(monthEntries, 4);
    const cappedTotal = capped.values.reduce((sum, value) => sum + value, 0);

    const pieData = {
        labels: capped.labels,
        datasets: [{
            label: BarChatName,
            data: capped.values,
            backgroundColor: capped.colors,
            borderColor: '#FFFFFF',
            borderWidth: 2,
            hoverOffset: 4,
        }],
    };

    const pieOptions = doughnutOptions({ valueFormatter: formatCount });
    const handleClickedCard = (x) => {
        const userDetailsFromStorage = JSON.parse(localStorage.getItem('userDetails'))
        setBarChatName(x)
        setActiveCardLabel(x)
        getDashboardChatData(userDetailsFromStorage, x)
    }
    const hasChartData = Boolean(labels && labels.length > 0);

    return (
        <>
            {Loader === false ? (
                <div className="ds-page dashboard-root">
                    <div className="ds-page-header">
                        <div className="ds-page-header__text">
                            <h1 className="ds-page-title">Dashboard</h1>
                            <p className="ds-subtle">
                                Your verification workload for {new Date().getFullYear()}
                            </p>
                        </div>
                    </div>

                    {/* ----------------------------------------------------------- */}
                    {/* Metric cards. Each is a filter for the two charts below, so  */}
                    {/* the selected one carries an explicit selected state rather   */}
                    {/* than only a colour change.                                   */}
                    {/* ----------------------------------------------------------- */}
                    {DashboardData.length > 0 ? (
                        <div
                            className="ds-grid ds-grid--kpi"
                            role="tablist"
                            aria-label="Select a metric to chart"
                        >
                            {DashboardData.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeCardLabel === item.label}
                                    className={`ds-card ds-stat ds-card--interactive card-dashboard ${activeCardLabel === item.label ? 'active' : ''}`}
                                    onClick={() => handleClickedCard(item.label)}
                                    disabled={chartLoader}
                                >
                                    <div className="ds-stat__top">
                                        <span className="ds-stat__label">{item.label}</span>
                                        <span className="ds-stat__icon" aria-hidden="true">
                                            <ClipboardCheck />
                                        </span>
                                    </div>
                                    {chartLoader && activeCardLabel === item.label ? (
                                        <span
                                            className="ds-skeleton"
                                            style={{ height: 30, width: '45%' }}
                                        />
                                    ) : (
                                        <span className="ds-stat__value">
                                            {formatCount(item.count)}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="ds-card ds-empty">
                            <span className="ds-empty__icon" aria-hidden="true">
                                <ClipboardCheck />
                            </span>
                            <p className="ds-empty__title">No activity yet</p>
                            <p className="ds-empty__body">
                                Once properties are assigned to you, your workload metrics will
                                appear here.
                            </p>
                        </div>
                    )}

                    {/* ----------------------------------------------------------- */}
                    {/* Charts. Two separate charts, each with its own scale.         */}
                    {/* ----------------------------------------------------------- */}
                    <div className="ds-grid ds-grid--wide charts">
                        <div className="ds-card chart-card fstchat">
                            <div className="ds-card__header">
                                <div className="ds-card__header-text">
                                    <h2 className="ds-card-title">
                                        {BarChatName || 'Monthly activity'}
                                    </h2>
                                    <p className="ds-caption">By month, current year</p>
                                </div>
                                <span className="ds-stat__meta">
                                    <span
                                        className="ds-legend__swatch"
                                        style={{ background: 'var(--ds-chart-1)' }}
                                        aria-hidden="true"
                                    />
                                    Count
                                </span>
                            </div>
                            <div className="ds-card__body">
                                {chartLoader ? (
                                    <div className="ds-chart chart-loader-text">
                                        <span
                                            className="ds-skeleton ds-skeleton--block"
                                            style={{ height: '100%' }}
                                        />
                                    </div>
                                ) : hasChartData ? (
                                    <div className="ds-chart">
                                        <BarCharts data={chartData} options={chartOptions} />
                                    </div>
                                ) : (
                                    <div className="ds-empty" style={{ minHeight: 200 }}>
                                        <span className="ds-empty__icon" aria-hidden="true">
                                            <BarChart3 />
                                        </span>
                                        <p className="ds-empty__title">No data for this metric</p>
                                        <p className="ds-empty__body">
                                            Select a different metric card above.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="ds-card chart-card schat">
                            <div className="ds-card__header">
                                <div className="ds-card__header-text">
                                    <h2 className="ds-card-title">Busiest months</h2>
                                    <p className="ds-caption">
                                        Share of {formatCount(cappedTotal)} total across the year
                                    </p>
                                </div>
                            </div>
                            <div className="ds-card__body">
                                {chartLoader ? (
                                    <div className="ds-chart ds-chart--short">
                                        <span
                                            className="ds-skeleton ds-skeleton--block"
                                            style={{ height: '100%' }}
                                        />
                                    </div>
                                ) : capped.values.length > 0 ? (
                                    <>
                                        <div className="ds-chart ds-chart--short">
                                            <PieCharts data={pieData} options={pieOptions} />
                                        </div>
                                        {/* Values live in the legend, so a segment is never
                                            identified by colour alone. */}
                                        <ul className="ds-legend month-legend">
                                            {capped.labels.map((label, index) => (
                                                <li className="ds-legend__item" key={label}>
                                                    <span
                                                        className="ds-legend__swatch"
                                                        style={{ background: capped.colors[index] }}
                                                        aria-hidden="true"
                                                    />
                                                    {label}
                                                    <span className="ds-legend__value">
                                                        {formatCount(capped.values[index])}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <div className="ds-empty" style={{ minHeight: 160 }}>
                                        <span className="ds-empty__icon" aria-hidden="true">
                                            <PieChartIcon />
                                        </span>
                                        <p className="ds-empty__title">Nothing to compare yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="loader-container" role="status" aria-live="polite" aria-busy="true">
                    <div className="loader-wrapper">
                        <div className="spinner"></div>
                        <img src={logo} className="logo-loader" alt="" aria-hidden="true" />
                    </div>
                    <p className="ds-caption">Loading dashboard…</p>
                </div>
            )}
        </>
    );
};

export default Dashboard;
